import Call from "@/components/common/Call";
import SmallEditDialog from "@/components/common/SmallEditDialog";
import EditInjectionDetailsOrthopaedicArea from "@/features/injection/components/EditInjectionDetailsOrthopaedicArea";
import EditInjectionDetailsSide from "@/features/injection/components/EditInjectionDetailsSide";
import SharedAttributeDatePicker from "@/features/shared_attributes/components/SharedAttributeDatePicker";
import { cn } from "@/lib/utils";
import type { StatefulDiaryT } from "@/types/database/diaries";
import { AlertCircle } from "lucide-react";
import { useMissingDetails } from "../hooks/useMissingDetails";

interface DiaryCardProps {
  diary: StatefulDiaryT;
  className?: string;
}

const MissingDiaryDetailsCall: React.FC<DiaryCardProps> = ({
  diary,
  className,
}) => {
  const { missingDetails, hasMissingDetails } = useMissingDetails(diary);

  if (!hasMissingDetails) {
    return null;
  }

  // Create a unique list of missing fields
  const hasMissingDate = missingDetails?.trackers?.missingDate?.length > 0;
  const hasMissingSide = missingDetails?.injections?.missingSide?.length > 0;
  const hasMissingOrthopaedicArea =
    missingDetails?.injections?.missingOrthopaedicArea?.length > 0;

  // Get the first injection detail that needs updating
  const injectionDetail = diary.events_view?.[0]?.injections_view?.[0]
    ?.injection_details?.[0];

  return (
    <Call
      icon={<AlertCircle size={24} />}
      words="Missing Information"
      variant="warning"
      size="sm"
      className={cn("pb-4", className)}
    >
      <ul className="space-y-1.5">
        {hasMissingDate && (
          <li>
            <SmallEditDialog label="Procedure Date">
              <SharedAttributeDatePicker
                sharedAttributes={diary.shared_attributes}
                dateType="reference_date"
              />
            </SmallEditDialog>
          </li>
        )}

        {hasMissingSide && injectionDetail?.injection_detail_id && (
          <li>
            <SmallEditDialog label="Side of Procedure">
              <EditInjectionDetailsSide
                diaryId={diary.diary_id}
                injectionDetailId={injectionDetail.injection_detail_id}
                side={injectionDetail.side ?? "N/A"}
              />
            </SmallEditDialog>
          </li>
        )}
        {hasMissingOrthopaedicArea && injectionDetail?.injection_detail_id && (
          <li>
            <SmallEditDialog label="Anatomic Area">
              <EditInjectionDetailsOrthopaedicArea
                diaryId={diary.diary_id}
                injectionDetailId={injectionDetail.injection_detail_id}
                orthopaedicArea={injectionDetail.orthopaedic_area ?? ""}
              />
            </SmallEditDialog>
          </li>
        )}
      </ul>
    </Call>
  );
};

export default MissingDiaryDetailsCall;
